
// If you already have a centralized http instance, swap this to use it.
// This matches your backend base paths: /api/admin/hero, /api/hero
import { http } from "./http";

export type HeroBackgroundType = "image" | "video" | "gradient";

export type HeroLayoutType = "hero" | "banner" | "mini_banner";

export type HeroContentAlign = "left" | "center" | "right";
export type HeroContentPosition = "left" | "center" | "right";
export type HeroMediaPosition = "none" | "left" | "right";
export type HeroBannerHeight = "compact" | "standard" | "tall";

export type HeroSettings = {
  id: string;
  headline: string;
  subheadline: string;
  hero_layout_type: HeroLayoutType;
  background_type: HeroBackgroundType;
  background_url: string | null;
  background_url_mobile: string | null;
  gradient_css: string | null;
  overlay_opacity: number;
  content_align: HeroContentAlign;
  content_position: HeroContentPosition;
  media_position: HeroMediaPosition;
  banner_height: HeroBannerHeight;
  carousel_enabled: boolean;

  primary_cta_text: string | null;
  primary_cta_link: string | null;
  secondary_cta_text: string | null;
  secondary_cta_link: string | null;

  promo_enabled: boolean;
  promo_text: string;

  loyalty_highlight: boolean;

  is_active: boolean;
  start_at: string | null;
  end_at: string | null;

  created_at: string;
  updated_at: string;
  placement: "home" | "shop" | "checkout" | "global" | "wholesale_home";
  visibility: "public" | "members" | "guests";
  display_priority: number;

};

export type HeroProductInput = {
  product_id: number;
  sort_order?: number;
  badge_text?: string | null;
  use_product_image?: boolean;
};

export type HeroProductRow = {
  hero_id: string;
  product_id: number;
  sort_order: number;
  badge_text: string | null;
  use_product_image: boolean;
  product: {
    id: number;
    slug: string;
    name: string;
    price: string | number;
    image_url: string | null;
  };
};

export async function adminListHeros(): Promise<HeroSettings[]> {
  const res = await http.get(`/api/admin/hero`, { withCredentials: true });
  return res.data;
}

export async function adminGetHero(id: string): Promise<HeroSettings> {
  const res = await http.get(`/api/admin/hero/${id}`, { withCredentials: true });
  return res.data;
}

export async function adminCreateHero(payload: Partial<HeroSettings>): Promise<HeroSettings> {
  const res = await http.post(`/api/admin/hero`, payload, { withCredentials: true });
  return res.data;
}

export async function adminUpdateHero(id: string, payload: Partial<HeroSettings>): Promise<HeroSettings> {
  const res = await http.patch(`/api/admin/hero/${id}`, payload, { withCredentials: true });
  return res.data;
}

export async function adminDeleteHero(id: string): Promise<{ ok: boolean; id: string }> {
  const res = await http.delete(`/api/admin/hero/${id}`, { withCredentials: true });
  return res.data;
}

export async function adminDuplicateHero(id: string): Promise<HeroSettings & { duplicated_products_count?: number }> {
  const res = await http.post(`/api/admin/hero/${id}/duplicate`, {}, { withCredentials: true });
  return res.data;
}

export async function adminGetHeroProducts(heroId: string): Promise<HeroProductRow[]> {
  const res = await http.get(`/api/admin/hero/${heroId}/products`, { withCredentials: true });
  return res.data;
}

export async function adminSetHeroProducts(heroId: string, products: HeroProductInput[]) {
  const res = await http.put(
    `/api/admin/hero/${heroId}/products`,
    { products },
    { withCredentials: true }
  );
  return res.data;
}

// Public (optional for preview parity)
export async function publicGetActiveHeros(params?: {
  placement?: "home" | "shop" | "checkout" | "global" | "wholesale_home";
  visibility?: "public" | "members" | "guests";
}): Promise<any[]> {
  const res = await http.get(`/api/hero/active`, {
    params,
    withCredentials: true,
  });

  return res.data;
}